// Per-host lifecycle: startup and teardown are separate barriers.
// Cleanup failure permanently blocks this host; a fresh page is required.
export function sessionLifecycle({ cleanupTimeoutMs = 10000 } = {}) {
  let generation = 0, pending = null, client = null, cleanupError = null;
  const closing = new Set();
  const disposed = new WeakMap();
  const isCurrent = token => token === generation && !cleanupError;
  function close(value) {
    if (!value) return Promise.resolve();
    if (disposed.has(value)) return disposed.get(value);
    let timer;
    // Defer invocation until the barrier has been registered, including sync throws.
    const teardown = Promise.resolve().then(() => value.endSession());
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error('Assistant cleanup timed out')), cleanupTimeoutMs);
    });
    const job = Promise.race([teardown, timeout]).catch(error => {
      cleanupError = error instanceof Error ? error : new Error('Assistant cleanup failed');
      throw cleanupError;
    }).finally(() => { clearTimeout(timer); closing.delete(job); });
    disposed.set(value, job);
    closing.add(job);
    return job;
  }
  return {
    begin() {
      if (pending !== null || client !== null || closing.size || cleanupError) return null;
      pending = ++generation;
      return pending;
    },
    isCurrent,
    async created(token, value) {
      if (!isCurrent(token)) { await close(value); return false; }
      client = value;
      return true;
    },
    expireStartup(token) {
      if (pending !== token || cleanupError) return false;
      // An unresolved SDK setup may still own a socket or microphone. Never release
      // its barrier on timeout; stale created callbacks will dispose late clients.
      cleanupError = new Error('Assistant startup timed out; reload required');
      generation += 1;
      return true;
    },
    finish(token) { if (pending === token) pending = null; },
    cancel() {
      generation += 1;
      const old = client;
      client = null;
      if (old) return close(old);
      if (closing.size) return Promise.all([...closing]).then(() => undefined);
      if (cleanupError) return Promise.reject(cleanupError);
      return Promise.resolve();
    },
    unexpectedDisconnect(token) {
      if (!isCurrent(token)) return;
      // SDK 1.26.0 emits onDisconnect in finally, even when teardown throws.
      // A spontaneous callback cannot prove tracks closed; block restart.
      cleanupError = new Error('Unexpected disconnect: cleanup is unverified');
      generation += 1;
    },
    getClient() { return client; },
    isPending() { return pending !== null; },
    isClosing() { return closing.size > 0; },
    getCleanupError() { return cleanupError; },
    // Client-side tool callbacks must be bound to their originating generation.
    async runTool(token, handler, args) {
      if (!isCurrent(token) || closing.size || !client) {
        return { success: false, code: 'STALE_SESSION' };
      }
      const result = await handler(args);
      return isCurrent(token) && !closing.size && client ? result : { success: false, code: 'STALE_SESSION' };
    }
  };
}
